import { Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../routes/routes';
import type { EmployeeSessionRow } from '../../apps/apis/claudeCodeDashboard/types';
import { HELP_TEXT } from './agentTelemetryHelp';
import { HelpBadge, HelpLabel } from './HelpTooltip';

interface Props {
  sessions: EmployeeSessionRow[];
}

const formatDate = (value: string | null): string => {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString();
};

const formatCost = (value: number | null): string => (value === null ? '—' : `$${value.toFixed(4)}`);

const EmployeeSessionList = ({ sessions }: Props) => {
  if (sessions.length === 0) {
    return <div className="text-muted small">No sessions in this window.</div>;
  }
  return (
    <Table size="sm" hover responsive>
      <thead>
        <tr>
          <th>
            <HelpLabel description="Agent session id. Open it to inspect the event timeline.">Session</HelpLabel>
          </th>
          <th>
            <HelpLabel description="First event timestamp for the session.">Started</HelpLabel>
          </th>
          <th>
            <HelpLabel description="Last/stop timestamp if the session ended normally.">Ended</HelpLabel>
          </th>
          <th className="text-end">
            <HelpLabel description={HELP_TEXT.events}>Events</HelpLabel>
          </th>
          <th className="text-end">
            <HelpLabel description={HELP_TEXT.inputTokens}>Input tok</HelpLabel>
          </th>
          <th className="text-end">
            <HelpLabel description={HELP_TEXT.outputTokens}>Output tok</HelpLabel>
          </th>
          <th className="text-end">
            <HelpLabel description={HELP_TEXT.costUsd}>Cost</HelpLabel>
          </th>
          <th>
            <HelpLabel description="Whether the session has an observed normal stop, failure, or is still in flight.">
              Status
            </HelpLabel>
          </th>
        </tr>
      </thead>
      <tbody>
        {sessions.map((session) => (
          <tr key={session.sessionId}>
            <td>
              <Link to={ROUTES.agentTelemetry.sessionDetail(session.sessionId)} className="text-monospace small">
                {session.sessionId.slice(0, 12)}…
              </Link>
            </td>
            <td className="small">{formatDate(session.startedAt)}</td>
            <td className="small">{formatDate(session.endedAt)}</td>
            <td className="text-end">{session.totalEvents?.toLocaleString() ?? '—'}</td>
            <td className="text-end">{session.totalInputTokens?.toLocaleString() ?? '—'}</td>
            <td className="text-end">{session.totalOutputTokens?.toLocaleString() ?? '—'}</td>
            <td className="text-end">{formatCost(session.totalCostUsd)}</td>
            <td>
              {session.endedNormally === null ? (
                <HelpBadge bg="secondary" description="No normal stop or failure event has been observed yet.">
                  In flight
                </HelpBadge>
              ) : session.endedNormally ? (
                <HelpBadge bg="success" description="The session ended with a normal stop event.">
                  OK
                </HelpBadge>
              ) : (
                <HelpBadge bg="danger" description="The session ended with a failure or abnormal stop signal.">
                  Failed
                </HelpBadge>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default EmployeeSessionList;
